/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: TimeGroupFormView.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    Form view for creating and editing Time Groups with schedules.
    Manages time-based routing schedules for ARS and Time Conditions.

 🔧 Notes:
    - Supports multiple schedules per time group
    - Day of week selection (multi-select)
    - Time range inputs (start/end time)
    - Optional date range for seasonal schedules
    - Domain-scoped data

 🔁 Last Updated: 2025-11-07 by GPT-5 Codex
────────────────────────────────────────────────────────────────*/

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  Calendar,
  Clock,
  Plus,
  Search,
  Trash2,
  X,
  Copy,
} from 'lucide-react';
import { fetchTimeGroup, createTimeGroup, updateTimeGroup, fetchTimeGroups } from './api/timeGroupApi';
import { TimeGroup } from './types';
import { ModuleFormHeader } from '../../components/layouts';
import { Button } from '../../components/ui/button';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import Toggle from '../../components/ui/Toggle';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useNavigationStore } from '../../store/navigation';
import { deleteTimeGroupById, navigateToTimeGroupDuplicate } from './timeGroupsActions';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { SchedulesSection } from './components/SchedulesSection';

interface Schedule {
  id?: number;
  dayOfWeek: string[] | null;
  startTime: string | null;
  endTime: string | null;
  startDate: string | null;
  endDate: string | null;
}

const DAYS_OF_WEEK = [
  { value: 'mon', label: 'Monday', labelShort: 'Mon' },
  { value: 'tue', label: 'Tuesday', labelShort: 'Tue' },
  { value: 'wed', label: 'Wednesday', labelShort: 'Wed' },
  { value: 'thu', label: 'Thursday', labelShort: 'Thu' },
  { value: 'fri', label: 'Friday', labelShort: 'Fri' },
  { value: 'sat', label: 'Saturday', labelShort: 'Sat' },
  { value: 'sun', label: 'Sunday', labelShort: 'Sun' },
];

const DEFAULT_SCHEDULE: Schedule = {
  dayOfWeek: ['mon', 'tue', 'wed', 'thu', 'fri'],
  startTime: '09:00',
  endTime: '17:00',
  startDate: null,
  endDate: null,
};

function deepClone<T>(value: T): T {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value));
}

const sortDays = (days: string[] | null) => {
  if (!Array.isArray(days)) return null;
  return [...days].sort();
};

const formatDateValue = (value: string | null | undefined) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : date.toISOString().split('T')[0];
};

export default function TimeGroupFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const resetParam = searchParams.get('_reset');
  const isEditMode = Boolean(id && id !== 'new');
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError, showInfo } = useNotifications();
  const { setCurrentPageTitle } = useNavigationStore();

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    enabled: true,
  });
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [originalData, setOriginalData] = useState({
    name: '',
    description: '',
    enabled: true,
  });
  const [originalSchedules, setOriginalSchedules] = useState<Schedule[]>([]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListSchedules, setQuickListSchedules] = useState<any[]>([]);

  const lastSaveTimestamp = useRef<number>(0);
  const hasInitialized = useRef<boolean>(false);
  const lastLoadedIdRef = useRef<string | null>(null);
  const isLoadingRef = useRef<boolean>(false);
  const currentIdRef = useRef<string | undefined>(undefined);
  const hasLoadedInitialDataRef = useRef<boolean>(false);
  const lastIdRef = useRef<string | undefined>(id);
  const lastResetParamRef = useRef<string | null>(null);

  // Stable refs for functions to prevent re-creations
  const showErrorRef = useRef(showError);
  const showSuccessRef = useRef(showSuccess);
  const showInfoRef = useRef(showInfo);
  const tRef = useRef(t);
  const navigateRef = useRef(navigate);

  useEffect(() => {
    showErrorRef.current = showError;
    showSuccessRef.current = showSuccess;
    showInfoRef.current = showInfo;
    tRef.current = t;
    navigateRef.current = navigate;
  }, [showError, showSuccess, showInfo, t, navigate]);

  // Reset form when navigating to 'new' after Save and New
  useEffect(() => {
    const currentId = id || 'new';
    const previousId = lastIdRef.current;
    const currentResetParam = resetParam;

    // If we're navigating from edit/create mode to 'new', reset the form
    // Also check for _reset query param which is set by useSaveWithAction
    const shouldReset = (!id || id === 'new') &&
      !isEditMode &&
      (previousId !== currentId ||
        (currentResetParam && currentResetParam !== lastResetParamRef.current));

    if (shouldReset) {
      setFormData({
        name: '',
        description: '',
        enabled: true,
      });
      setSchedules([deepClone(DEFAULT_SCHEDULE)]);
      setOriginalData({
        name: '',
        description: '',
        enabled: true,
      });
      setOriginalSchedules([deepClone(DEFAULT_SCHEDULE)]);
      setErrors({});
      hasInitialized.current = true;

      // Clear the reset param from URL to prevent repeated resets
      if (currentResetParam) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('_reset');
        const newSearch = newSearchParams.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
      }
    }

    lastIdRef.current = currentId;
    lastResetParamRef.current = currentResetParam;
  }, [id, isEditMode, resetParam, searchParams]);

  const pageTitle = useMemo(
    () => (isEditMode ? `${t('timeGroups.editGroup')} - ${formData.name || ''}` : t('timeGroups.createGroup')),
    [isEditMode, t, formData.name],
  );

  const normalizeSchedule = (raw: unknown): Schedule => {
    const r = raw as Record<string, unknown>;
    return {
      id: r.id as number | undefined,
      dayOfWeek: Array.isArray(r.day_of_week ?? r.dayOfWeek)
        ? [...((r.day_of_week ?? r.dayOfWeek) as string[])]
        : null,
      startTime: ((r.start_time ?? r.startTime ?? null) as string | null)?.substring(0, 5) ?? null,
      endTime: ((r.end_time ?? r.endTime ?? null) as string | null)?.substring(0, 5) ?? null,
      startDate: formatDateValue((r.start_date ?? r.startDate) as string | null | undefined),
      endDate: formatDateValue((r.end_date ?? r.endDate) as string | null | undefined),
    };
  };

  const loadTimeGroupFn = useCallback(
    async (timeGroupId: string, duplicate = false) => {
      // Prevent duplicate loads
      if (isLoadingRef.current || lastLoadedIdRef.current === timeGroupId) {
        return;
      }

      isLoadingRef.current = true;
      lastLoadedIdRef.current = timeGroupId;
      setIsLoading(true);

      try {
        const group = await fetchTimeGroup(timeGroupId);
        const loadedFormData = {
          name: duplicate
            ? `${group.name || ''} ${tRef.current('common.copySuffix')}`.trim()
            : group.name || '',
          description: group.description || '',
          enabled: group.enabled ?? true,
        };

        const loadedSchedules = Array.isArray(group.schedules)
          ? group.schedules.map(normalizeSchedule)
          : [];

        // Batch state updates to prevent re-renders during render cycle
        setTimeout(() => {
          setFormData(loadedFormData);
          setSchedules(loadedSchedules.length > 0 ? loadedSchedules : []);

          if (duplicate) {
            setOriginalData({
              name: '',
              description: '',
              enabled: true,
            });
            setOriginalSchedules([]);
          } else {
            setOriginalData(loadedFormData);
            setOriginalSchedules(deepClone(loadedSchedules));
          }
        }, 0);
      } catch (error) {
        lastLoadedIdRef.current = null; // Allow retry on error
        showErrorRef.current(tRef.current('timeGroups.messages.loadError'));
        navigateRef.current('/pbx/incoming-tools/time-groups');
      } finally {
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    },
    [],
  );

  const loadTimeGroupRef = useRef(loadTimeGroupFn);
  useEffect(() => {
    loadTimeGroupRef.current = loadTimeGroupFn;
  }, [loadTimeGroupFn]);

  // Load initial data only once
  useEffect(() => {
    if (hasLoadedInitialDataRef.current) return;
    hasLoadedInitialDataRef.current = true;
  }, []);

  // Reset load guard on unmount — ensures navigating back to the same ID always reloads fresh data
  useEffect(() => {
    return () => {
      lastLoadedIdRef.current = null;
      currentIdRef.current = undefined;
    };
  }, []);

  // Main effect for loading data when id/duplicateId changes
  useEffect(() => {
    const loadId = duplicateId || id;
    const idChanged = currentIdRef.current !== loadId;

    if (idChanged) {
      currentIdRef.current = loadId;
    }

    if (isEditMode && id && idChanged) {
      setTimeout(() => {
        loadTimeGroupRef.current(id, false);
      }, 0);
    } else if (duplicateId && idChanged) {
      setTimeout(() => {
        loadTimeGroupRef.current(duplicateId, true);
      }, 0);
    } else if (!isEditMode && !duplicateId && !hasInitialized.current) {
      setTimeout(() => {
        lastLoadedIdRef.current = null;
        setSchedules([deepClone(DEFAULT_SCHEDULE)]);
        hasInitialized.current = true;
      }, 0);
    }
  }, [duplicateId, id, isEditMode]);

  const handleChange = (
    field: 'name' | 'description' | 'enabled',
    value: unknown,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const schedulesComparable = useMemo(
    () =>
      schedules.map((schedule) => ({
        ...schedule,
        dayOfWeek: sortDays(schedule.dayOfWeek),
      })),
    [schedules],
  );

  const originalSchedulesComparable = useMemo(
    () =>
      originalSchedules.map((schedule) => ({
        ...schedule,
        dayOfWeek: sortDays(schedule.dayOfWeek),
      })),
    [originalSchedules],
  );

  const hasChanges = useMemo(() => {
    if (
      formData.name !== originalData.name ||
      formData.description !== originalData.description ||
      formData.enabled !== originalData.enabled
    ) {
      return true;
    }

    if (schedulesComparable.length !== originalSchedulesComparable.length) {
      return true;
    }

    return (
      JSON.stringify(schedulesComparable) !==
      JSON.stringify(originalSchedulesComparable)
    );
  }, [
    formData,
    originalData,
    schedulesComparable,
    originalSchedulesComparable,
  ]);

  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};

    if (!formData.name || formData.name.trim().length === 0) {
      newErrors.name = t('timeGroups.validation.nameRequired');
    } else if (formData.name.trim().length < 3) {
      newErrors.name = t('timeGroups.validation.nameMinLength');
    }

    if (schedules.length === 0) {
      newErrors.schedules = t('timeGroups.validation.scheduleRequired');
    } else {
      schedules.forEach((schedule, index) => {
        if (!schedule.dayOfWeek || schedule.dayOfWeek.length === 0) {
          newErrors[`schedule_${index}_days`] = t(
            'timeGroups.validation.daysRequired',
          );
        }

        if (!schedule.startTime || !schedule.endTime) {
          newErrors[`schedule_${index}_time`] = t(
            'timeGroups.validation.timeRequired',
          );
        } else if (schedule.startTime >= schedule.endTime) {
          newErrors[`schedule_${index}_time`] = t(
            'timeGroups.validation.timeOrder',
          );
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData.name, schedules, t]);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent): Promise<TimeGroup | undefined> => {
      e?.preventDefault();

      // Double-save prevention check before setIsSaving
      const now = Date.now();
      if (now - lastSaveTimestamp.current < 1000) {
        return undefined;
      }
      lastSaveTimestamp.current = now;

      if (!validateForm()) {
        showErrorRef.current(tRef.current('common.validationError'));
        return undefined;
      }
      setIsSaving(true);
      try {
        // Filter out invalid schedules (must have at least one day and both times)
        const validSchedules = schedules.filter(
          (schedule) => {
            // Validate dayOfWeek
            if (!schedule.dayOfWeek || !Array.isArray(schedule.dayOfWeek) || schedule.dayOfWeek.length === 0) {
              return false;
            }

            // Validate startTime and endTime - must be valid strings matching HH:MM or HH:MM:SS format
            const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/;
            if (!schedule.startTime || typeof schedule.startTime !== 'string' || !timeRegex.test(schedule.startTime.trim())) {
              return false;
            }
            if (!schedule.endTime || typeof schedule.endTime !== 'string' || !timeRegex.test(schedule.endTime.trim())) {
              return false;
            }

            return true;
          }
        );

        const payload = {
          name: formData.name.trim(),
          description: formData.description?.trim() || null,
          enabled: formData.enabled,
          schedules: validSchedules.map((schedule) => ({
            dayOfWeek: schedule.dayOfWeek ?? [],
            startTime: schedule.startTime && typeof schedule.startTime === 'string' ? schedule.startTime : null,
            endTime: schedule.endTime && typeof schedule.endTime === 'string' ? schedule.endTime : null,
            startDate: schedule.startDate || null,
            endDate: schedule.endDate || null,
          })),
        };

        let result;
        if (isEditMode && id) {
          result = await updateTimeGroup(id, payload);
        } else {
          result = await createTimeGroup(payload);
        }

        showSuccessRef.current(
          isEditMode
            ? tRef.current('timeGroups.messages.updateSuccess')
            : tRef.current('timeGroups.messages.createSuccess'),
        );

        setOriginalData({
          name: payload.name,
          description: payload.description || '',
          enabled: payload.enabled,
        });
        setOriginalSchedules(deepClone(schedules));

        // Reset load guard so saving then re-editing always fetches fresh data from server
        lastLoadedIdRef.current = null;
        currentIdRef.current = undefined;

        // Return saved data for useSaveWithAction
        return result;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        showErrorRef.current(error.message || tRef.current('timeGroups.messages.createError'));
        return undefined;
      } finally {
        setIsSaving(false);
      }
    },
    [
      formData,
      hasChanges,
      id,
      isEditMode,
      schedules,
      validateForm,
    ],
  );

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      // Return current data with ID/UUID for navigation purposes
      return { id: Number(id), uuid: String(id) } as TimeGroup;
    }

    return savedData;
  }, [handleSubmit, isEditMode, id]);

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/incoming-tools/time-groups',
    newPath: '/pbx/incoming-tools/time-groups/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => {
      // Time Groups use ID for navigation
      return `/pbx/incoming-tools/time-groups/${savedData.id || savedData.uuid}`;
    },
    getFormData: () => ({
      formData,
      schedules
    }),
    storageKey: 'timeGroups:saveAndNewData'
  });

  const handleCancel = () => {
    if (isEditMode) {
      setFormData(deepClone(originalData));
      setSchedules(deepClone(originalSchedules));
    } else {
      setFormData({
        name: '',
        description: '',
        enabled: true,
      });
      setSchedules([deepClone(DEFAULT_SCHEDULE)]);
      hasInitialized.current = true;
    }
    setErrors({});
  };

  const handleDeleteClick = () => {
    if (!isEditMode || !id) return;
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!isEditMode || !id) return;

    setIsDeleting(true);
    try {
      await deleteTimeGroupById(id);
      showSuccess(t('timeGroups.messages.deleteSuccess'));
      navigate('/pbx/incoming-tools/time-groups');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      showError(error.message || t('timeGroups.messages.deleteError'));
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleDuplicateFromForm = () => {
    if (!isEditMode || !id) return;
    navigateToTimeGroupDuplicate(navigate, id);
  };

  const addSchedule = () => {
    setSchedules((prev) => [...prev, deepClone(DEFAULT_SCHEDULE)]);
  };

  const updateSchedule = (index: number, field: keyof Schedule, value: Schedule[keyof Schedule]) => {
    setSchedules((prev) =>
      prev.map((schedule, currentIndex) =>
        currentIndex === index ? { ...schedule, [field]: value } : schedule,
      ),
    );
  };

  const removeSchedule = (index: number) => {
    setSchedules((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
  };

  const toggleDay = (index: number, day: string) => {
    setSchedules((prev) =>
      prev.map((schedule, currentIndex) => {
        if (currentIndex !== index) return schedule;
        const currentDays = schedule.dayOfWeek ?? [];
        const nextDays = currentDays.includes(day)
          ? currentDays.filter((d) => d !== day)
          : [...currentDays, day];
        return { ...schedule, dayOfWeek: nextDays.length > 0 ? nextDays : null };
      }),
    );
  };

  const loadQuickList = useCallback(async () => {
    try {
      const result = await fetchTimeGroups(
        {
          page: 1,
          limit: 100,
          sortBy: 'name',
          sortOrder: 'asc',
        },
        undefined,
        'name',
      );
      setQuickListSchedules(Array.isArray(result.data) ? result.data : []);
    } catch {
      setQuickListSchedules([]);
    }
  }, []);

  useEffect(() => {
    if (showQuickList) {
      loadQuickList();
    }
  }, [loadQuickList, showQuickList]);

  const handleQuickListSelect = (selectedSchedule: Schedule) => {
    const newSchedule: Schedule = {
      dayOfWeek: Array.isArray(selectedSchedule.dayOfWeek)
        ? [...selectedSchedule.dayOfWeek]
        : ['mon', 'tue', 'wed', 'thu', 'fri'],
      startTime: selectedSchedule.startTime || '09:00',
      endTime: selectedSchedule.endTime || '17:00',
      startDate: selectedSchedule.startDate || null,
      endDate: selectedSchedule.endDate || null,
    };
    setSchedules((prev) => [...prev, newSchedule]);
    setShowQuickList(false);
    setQuickListSearch('');
  };

  const filteredQuickList = useMemo(() => {
    if (!quickListSearch) return quickListSchedules;
    const term = quickListSearch.toLowerCase();
    return quickListSchedules.filter(
      (schedule) =>
        schedule.name?.toLowerCase().includes(term) ||
        schedule.description?.toLowerCase().includes(term),
    );
  }, [quickListSchedules, quickListSearch]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="p-6 pb-24">
      <ModuleFormHeader
        icon={<Clock className="h-5 w-5 text-primary" />}
        title={pageTitle}
        backButtonText={t('timeGroups.actions.list')}
        onBack={() => navigate('/pbx/incoming-tools/time-groups')}
        actions={[
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('timeGroups.actions.quickList'),
            onClick: () => setShowQuickList(true),
          },
          ...(isEditMode
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('common.duplicate'),
              onClick: handleDuplicateFromForm,
            }]
            : []),
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('timeGroups.actions.add'),
            onClick: () => navigate('/pbx/incoming-tools/time-groups/new'),
          },
        ]}
      />

      <form onSubmit={handleSubmit} className="mt-8 space-y-6">
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold">
              {t('timeGroups.sections.general')}
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
            <Label htmlFor="name">
              <SimpleTooltip content={t('timeGroups.fields.name.tooltip')}>
                <span>
                  {t('timeGroups.fields.name.label')}
                  <span className="text-red-500 ml-1">*</span>
                </span>
              </SimpleTooltip>
            </Label>
            <div>
              <Input
                id="name"
                value={formData.name}
                onChange={(event) => handleChange('name', event.target.value)}
                placeholder={t('timeGroups.fields.name.placeholder')}
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && (
                <p className="text-sm text-red-500 mt-1">{errors.name}</p>
              )}
            </div>

            <Label htmlFor="enabled">
              <SimpleTooltip content={t('timeGroups.fields.enabled.tooltip')}>
                <span>{t('timeGroups.fields.enabled.label')}</span>
              </SimpleTooltip>
            </Label>
            <div>
              <Toggle
                value={formData.enabled}
                onChange={(value) => handleChange('enabled', value)}
              />
            </div>

            <Label htmlFor="description">
              <SimpleTooltip content={t('timeGroups.fields.description.tooltip')}>
                <span>{t('timeGroups.fields.description.label')}</span>
              </SimpleTooltip>
            </Label>
            <div className="col-span-3">
              <Input
                id="description"
                value={formData.description}
                onChange={(event) => handleChange('description', event.target.value)}
                placeholder={t('timeGroups.fields.description.placeholder')}
              />
            </div>
          </div>
        </div>

        <SchedulesSection
          schedules={schedules}
          errors={errors}
          onAddSchedule={addSchedule}
          onRemoveSchedule={removeSchedule}
          onUpdateSchedule={updateSchedule}
          onToggleDay={toggleDay}
        />

        <FixedActionBar
          onSaveWithAction={handleSaveWithAction}
          onCancel={handleCancel}
          onDelete={handleDeleteClick}
          isEditMode={isEditMode}
          isSaving={isSaving}
          isLoading={isLoading}
          saveText={t('timeGroups.actions.save')}
          cancelText={t('timeGroups.actions.cancel')}
          deleteText={t('common.delete')}
          useSaveDropdown={true}
          saveDropdownStorageKey="timeGroups:saveActionPreference"
        />
      </form>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('timeGroups.messages.confirmDeleteTitle')}
        message={t('timeGroups.messages.confirmDeleteMessage', {
          name: formData.name || 'Time Group',
        })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
        }}
        title={t('timeGroups.actions.quickListTitle')}
        items={filteredQuickList}
        isLoadingInitial={false}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(schedule) => handleQuickListSelect(schedule)}
        renderItem={(schedule) => (
          <button
            type="button"
            onClick={() => handleQuickListSelect(schedule)}
            className="w-full rounded-md border border-border p-3 text-left transition-colors hover:bg-muted/50"
          >
            <div className="font-medium text-foreground">
              {schedule.name}
            </div>
            {schedule.description && (
              <div className="mt-1 text-sm text-muted-foreground">
                {schedule.description}
              </div>
            )}
            <div className="mt-1 text-xs text-muted-foreground">
              {schedule._count?.schedules || 0}{' '}
              {t('timeGroups.scheduleCount')}
            </div>
          </button>
        )}
        searchPlaceholder={t('timeGroups.actions.quickListSearch')}
        emptyMessage={t('timeGroups.actions.quickListNoResults')}
        headerIcon={<Clock className="h-5 w-5" />}
        minSearchChars={0}
      />
    </div>
  );
}
